/* global dataLayer */
class MultistepWizard extends HTMLElement {
  constructor() {
    super()
    this.path = []
    this.respuestas = []
    this.layoutBlock = this.closest('[data-layout]')
    this.form = this.querySelector('form')
    this.results = this.querySelector('multistep-results')
    this.ajaxSuccessCallback = undefined
  }

  connectedCallback() {
    // Fallback para navegaciones dentro de <turbo-frame id="content">: en esas
    // navegaciones se dispara turbo:frame-load (no turbo:load), así que el
    // handler de turbo:load de abajo nunca corre. Si no atáramos el submit
    // aquí también, el form quedaría sin intercept y haría submit nativo a la
    // action URL, mostrando el partial como text/plain HTML crudo.
    // El propio setupFormSubmit es idempotente vía `_submitListenerAttached`
    // para no duplicar el listener cuando turbo:load sí se dispara.
    this.setupFormSubmit()

    document.addEventListener('turbo:load', () => {
      this.showStep(this.getInitialStep())
      this.checkCrossOrigin()
      this.frameElement = this.crossorigin ? false : window.frameElement
      this.setupFormSubmit()

      if (this.crossorigin || this.frameElement) {
        this.layoutBlock.classList.add('iframed')
        this.frameAutoheight()
      }

      if (this.layoutBlock.dataset.layout === 'none') {
        this.layoutBlock.classList.add('landing-layout-page-wizard')
      }
      else if (this.layoutBlock.dataset.layout === 'application') {
        this.layoutBlock.classList.add('full-layout-page-wizard')
      }
    })

    this.addEventListener('next', function (event) {
      const stepOrigin = event.target

      stepOrigin.hide()
      this.path.push(stepOrigin.position)
      if (stepOrigin.position === 'start') {
        this.respuestas.push('start')
      } else {
        this.respuestas.push(stepOrigin.answers.value)
      }

      if (stepOrigin.getAttribute('go') === 'end') {
        this.path.push('end')
        this.respuestas.push('end')
        this.form.commit.click()
        location.hash = 'end'
      } else {
        const nextStep = this.findStep(stepOrigin.next)
        nextStep.prev = stepOrigin.position
        this.showStep(nextStep, { focus: true })
      }
    })

    this.addEventListener('prev', function (event) {
      const stepOrigin = event.target
      stepOrigin.hide()
      const prevStep = this.findStep(stepOrigin.prev)
      this.showStep(prevStep, { focus: true })
      this.path.pop()
      this.respuestas.pop()
    })

    document.addEventListener('turbo:render', () => {
      this.frameAutoheight()
    })
  }


  nofitySuccessToGA() {
    try {
      dataLayer.push({
        event: 'GAEvent',
        eventCategory: 'captadores',
        eventAction: 'captador_completed',
        eventLabel: `${this.dataset.captador}_${location.href}`,
        eventValue: 0
      })
    } catch (exc) {
      throw exc.message
    }
  }

  get omitingStart() {
    return (new URLSearchParams(location.search)).has('omit_start')
  }

  // `focus: true` solo en navegación por gesto del usuario (next/prev): en los
  // shows iniciales (turbo:load, connectedCallback de hipotecas) enfocar robaría
  // el scroll y en móvil levantaría el teclado sin que el usuario haya hecho nada.
  showStep(stepElem, { focus = false } = {}) {
    if (stepElem) {
      location.hash = stepElem.getAttribute('position')
      stepElem.show({ focus })
    }
  }

  checkCrossOrigin() {
    try {
      this.crossorigin = Object(window.parent.location).keys || false
    } catch (ex) {
      this.crossorigin = true
    }
  }

  frameAutoheight() {
    let evHandlers = {
      onChange: () => {
        const timer = setTimeout(() => {
          this.frameElement.height = this.offsetHeight
          window.clearTimeout(timer)
        }, 500)
      },
      onRetry: () => {
        let windowOffset = window.parent.pageYOffset
        let frameTop = this.frameElement.getBoundingClientRect().top
        window.parent.scrollTo(0, windowOffset + frameTop)
      }
    }

    if (this.frameElement) {
      evHandlers.onChange()
    } else {
      let postHeight = () => {
        window.parent.postMessage('rankia-height-' + this.offsetHeight, '*')
      }
      postHeight()

      evHandlers = {
        onChange: () => {
          const timer = setTimeout(() => {
            postHeight()
            window.clearTimeout(timer)
          }, 500)
        },
        onRetry: () => {
          window.parent.postMessage('rankia-retry', '*')
        }
      }
    }

    this.addEventListener('show', evHandlers.onChange)
    document.addEventListener('turbo:load', evHandlers.onChange)
  }

  setupFormSubmit() {
    if (this._submitListenerAttached) return
    this._submitListenerAttached = true
    this.form.addEventListener('submit', (event) => {
      event.preventDefault()

      if (!this.form.checkValidity()) {
        alert('Campos requeridos pendientes')
        return
      }

      // Normalizar inputs con formato local (int-local-format, dec-local-format):
      // su listener de submit en multistep-answers convierte "95.000" a "95000",
      // pero como nuestro listener se atacha en connectedCallback (antes que el
      // de multistep-answers, que usa onceReady.then() async), nuestro listener
      // dispararía primero y leería el valor sin normalizar. Lo hacemos aquí
      // para no depender del orden de listeners.
      this.form.querySelectorAll('input').forEach((input) => {
        if (typeof input.originalValue === 'function') {
          input.removeAttribute('pattern')
          input.value = input.originalValue()
        }
      })

      this.form.path.value = this.path.reverse().join()

      document.body.dataset.loading = 'Procesando'
      window.scrollTo(0, 0)
      this.frameAutoheight()

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      fetch(this.form.action, {
        method: this.form.method || 'POST',
        body: new FormData(this.form),
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': csrfToken, 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(response => {
          if (!response.ok) throw response
          return response.text()
        })
        .then(html => {
          this.results.innerHTML = html
          this.results.querySelectorAll('script').forEach(script => {
            const newScript = document.createElement('script')
            newScript.textContent = script.textContent
            script.replaceWith(newScript)
          })
          delete document.body.dataset.loading
          window.scrollTo(0, 0)
          this.frameAutoheight()
          if (typeof this.ajaxSuccessCallback === 'function') this.ajaxSuccessCallback()
          this.nofitySuccessToGA()
        })
        .catch(() => {
          delete document.body.dataset.loading
        })
    })
  }

  findStep(position) {
    return this.querySelector(`multistep-step[position="${position}"]`)
  }

  getInitialStep() {
    const initialSteps = [
      'start',
      'tipo',
      'importe_inversion',
      'nivel'
    ]

    return initialSteps
      .map(step => this.findStep(step))
      .find(step => step !== null)
  }
}

export default MultistepWizard
